import React from 'react';
import { Plus } from 'lucide-react';
import { HelpTip } from '../../ui/HelpTip';
import { NotionSelect } from '../../ui/notion';

export interface Choice<T extends string> { value: T; label: string; disabled?: boolean }

/** Palette dei pallini colore (stessa di Stati e fonti): un clic passa al colore successivo. */
export const COLOR_PALETTE = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16', '#F97316', '#EC4899', '#6B7280'];

export function nextColor(current: string, palette: string[] = COLOR_PALETTE): string {
  const i = palette.findIndex(c => c.toLowerCase() === current.toLowerCase());
  return palette[(i + 1) % palette.length];
}

/** Pallino colore cliccabile, senza riquadro. */
export function ColorDot({ color, onChange, label }: { color: string; onChange: (c: string) => void; label: string }) {
  return (
    <button type="button" aria-label={`Cambia colore: ${label}`} title="Cambia colore" onClick={() => onChange(nextColor(color))}
      className="w-3 h-3 rounded-full flex-shrink-0 hover:scale-125 transition-transform" style={{ backgroundColor: color }} />
  );
}

const compactSelect = '!py-1 !pl-2 !pr-7 !text-[13px] !border-transparent !bg-transparent hover:!bg-[rgba(55,53,47,0.06)] dark:hover:!bg-white/[0.06] cursor-pointer';

/** Scelta da elenco in linea, senza riquadro: il bordo compare solo al passaggio o al focus. */
export function ChoiceSelect<T extends string>({ value, onChange, choices, label, placeholder, width = 'auto' }: {
  value: T | ''; onChange: (v: T) => void; choices: Choice<T>[]; label: string; placeholder?: string; width?: 'auto' | 'grow';
}) {
  return (
    <div className={width === 'grow' ? 'flex-1 min-w-[140px]' : 'min-w-0 max-w-full'}>
      <NotionSelect aria-label={label} value={value} placeholder={placeholder} className={compactSelect}
        options={choices} onChange={e => onChange(e.target.value as T)} />
    </div>
  );
}

/** Una voce di impostazione: etichetta a sinistra (con "?" accanto), scelta a destra. */
export function SettingRow({ title, hint, help, example, children }: {
  title: string; hint?: string; help?: React.ReactNode; example?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 min-h-[40px] py-1.5 border-b border-[rgba(55,53,47,0.09)] dark:border-white/10">
      <div className="flex-1 min-w-[220px]">
        <div className="flex items-center gap-1.5 text-[13.5px] text-[#37352F] dark:text-white">
          {title}{help && <HelpTip title={title} text={help} example={example} />}
        </div>
        {hint && <div className="text-xs text-[#787774] dark:text-gray-400">{hint}</div>}
      </div>
      <div className="flex items-center gap-2 max-w-full">{children}</div>
    </div>
  );
}

/** Titolo di sezione: stesso stile del titolo delle sezioni del pannello dettagli. */
export function SectionTitle({ children, help }: { children: React.ReactNode; help?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5 mt-7 mb-1 text-[11px] font-medium uppercase tracking-wide text-[rgba(55,53,47,0.5)] dark:text-gray-400">
      {children}{help}
    </div>
  );
}

/** Azione discreta: testo grigio con più, nessun pulsante. */
export function LinkAction({ children, onClick, plus = true, disabled }: { children: React.ReactNode; onClick: () => void; plus?: boolean; disabled?: boolean }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      className="inline-flex items-center gap-1.5 text-sm text-[#787774] dark:text-gray-400 hover:text-[#37352F] dark:hover:text-white px-1.5 py-1 -mx-1.5 rounded hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/[0.06] transition-colors disabled:opacity-40">
      {plus && <Plus className="h-3.5 w-3.5" />}{children}
    </button>
  );
}
